import { Component, OnDestroy, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { isInvalid } from '../../../../shared/helpers/validate';
import { Subscription } from 'rxjs';
import { AddressDetailObject } from '@it2go/types';
import { CreateOrganizationPageStore } from './create-organization-page.store';
import { createOrganizationFrag } from './create-organization-page.query';
import { BaseDialogService } from '../../../../../components/kit/base-dialog/base-dialog-service';
import { OrganizationSettingTypeEnum } from '@libs/types/src/organization/enum/organization-setting-type.enum';
import {
    CrmNewOrganizationWizardDialogComponent
} from '../../../../crm/components/dialogs/crm-new-organization-wizard-dialog/crm-new-organization-wizard-dialog.component';
import { HeaderComponent } from '../../../../shared/components/kit/header/header.component';
import { DateInputComponent } from '../../../../../components/kit/input/date-input/date-input.component';
import { TextInputComponent } from '../../../../../components/kit/input/text-input/text-input.component';
import { WebInputComponent } from '../../../../../components/kit/input/text-input/web-input.component';
import { PhoneInputComponent } from '../../../../../components/kit/input/phone-input/phone-input.component';
import { EmailInputComponent } from '../../../../../components/kit/input/text-input/email-input.component';
import { SelectInputComponent } from '../../../../../components/kit/input/select-input/select-input.component';
import { AddressInputComponent } from '../../../../../components/kit/input/address-input/address-input.component';
import { ActionButtonsComponent } from '../../../../../components/kit/action-buttons/action-buttons.component';
import { TranslatePipe } from '@ngx-translate/core';
import { featureOn } from '../../../../../feature-flag';

@Component({
    selector: 'app-create-organization-page',
    templateUrl: './create-organization-page.component.html',
    styleUrls: ['./create-organization-page.component.scss'],
    providers: [CreateOrganizationPageStore],
    imports: [
        ActionButtonsComponent,
        AddressInputComponent,
        DateInputComponent,
        EmailInputComponent,
        HeaderComponent,
        PhoneInputComponent,
        ReactiveFormsModule,
        SelectInputComponent,
        TextInputComponent,
        TranslatePipe,
        WebInputComponent,
    ],
})
export class CreateOrganizationPageComponent implements OnDestroy {
    protected readonly featureOn = featureOn;
    protected readonly store = inject(CreateOrganizationPageStore);
    private readonly router = inject(Router);
    private readonly fb = inject(FormBuilder);
    private readonly dialogService = inject(BaseDialogService);


    form = this.fb.group({
        validFrom: this.fb.control<Date | null>(new Date(), Validators.required),
        systemFrom: this.fb.control<Date | null>(new Date(), Validators.required),
        systemTo: this.fb.control<Date | null>(null),
        name: this.fb.control<string | null>(null, Validators.required),
        ic: this.fb.control<string | null>(null),
        dic: this.fb.control<string | null>(null),
        web: this.fb.control<string | null>(null),
        phone: this.fb.control<string | null>(null),
        email: this.fb.control<string | null>(null, [Validators.email]),
        country: this.fb.control<string | null>('cz'),
        parentOrganizationId: this.fb.control<number | null>(null),
        // representativePerson: this.fb.group(this.createPersonFormGroup('representative')),
        // contactPerson: this.fb.group(this.createPersonFormGroup('contact')),
        address: this.fb.control<AddressDetailObject | null>(null, Validators.required),
    });

    countriesOptions = [{ name: 'cz' }, { name: 'sk' }, { name: 'pl' }, { name: 'ua' }, { name: 'at' }, { name: 'de' }];
    organizationSubscription: Subscription = new Subscription();

    protected prefix = 'organization.';

    ngOnDestroy() {
        this.organizationSubscription.unsubscribe();
    }

    async submit() {
        await this.createOrganization();
    }

    async hide() {
        await this.router.navigateByUrl('/content/organizations');
    }

    private async createOrganization(): Promise<void> {
        this.updateValidators();
        if (isInvalid(this.form)) return;

        const organization = await this.store.api.createOrganization({
            validFrom: this.form.value.validFrom!.toISOString(),
            systemFrom: this.form.value.systemFrom!.toISOString(),
            systemTo: this.form.value.systemTo?.toISOString() || null,
            name: this.form.value.name!,
            ic: this.form.value.ic ?? '',
            dic: this.form.value.dic ?? '',
            web: this.form.value.web ?? '',
            phone: this.form.value.phone ?? '',
            email: this.form.value.email ?? '',
            country: this.form.value.country ?? '',
            parentOrganizationId: this.form.value.parentOrganizationId ?? null,
            // representativePerson: {
            //     validFrom: this.form.value.representativePerson?.['representativeDateFrom']?.toString() ?? '',
            //     chosenPersonId:
            //         (this.form.value.representativePerson?.['representativeChosenPersonId'] as number) ?? null,
            //     titleBeforeName:
            //         this.form.value.representativePerson?.['representativeTitleBeforeName']?.toString() ?? null,
            //     name: this.form.value.representativePerson?.['representativeName']?.toString() ?? null,
            //     surname: this.form.value.representativePerson?.['representativeSurname']?.toString() ?? null,
            //     titleAfterName:
            //         this.form.value.representativePerson?.['representativeTitleAfterName']?.toString() ?? null,
            //     email: this.form.value.representativePerson?.['representativeEmail']?.toString() ?? null,
            //     password: this.form.value.representativePerson?.['representativePassword']?.toString() ?? null,
            //     role: this.form.value.representativePerson?.['representativeRole']?.toString() ?? null,
            //     sex: this.form.value.representativePerson?.['representativeSex']?.toString() ?? null,
            //     note: this.form.value.representativePerson?.['representativeNote']?.toString() ?? null,
            // },
            // contactPerson: {
            //     validFrom: this.form.value.contactPerson?.['contactDateFrom']?.toString() ?? '',
            //     chosenPersonId: (this.form.value.contactPerson?.['contactChosenPersonId'] as number) ?? null,
            //     titleBeforeName: this.form.value.contactPerson?.['contactTitleBeforeName']?.toString() ?? null,
            //     name: this.form.value.contactPerson?.['contactName']?.toString() ?? null,
            //     surname: this.form.value.contactPerson?.['contactSurname']?.toString() ?? null,
            //     titleAfterName: this.form.value.contactPerson?.['contactTitleAfter']?.toString() ?? null,
            //     email: this.form.value.contactPerson?.['contactEmail']?.toString() ?? null,
            //     password: this.form.value.contactPerson?.['contactPassword']?.toString() ?? null,
            //     role: this.form.value.contactPerson?.['contactRole']?.toString() ?? null,
            //     sex: this.form.value.contactPerson?.['contactSex']?.toString() ?? null,
            //     note: this.form.value.contactPerson?.['contactNote']?.toString() ?? null,
            // },
            address: {
                name: this.form.value.address?.['name'] || '',
                location: this.form.value.address?.['location'] || '',
                position: {
                    lat: this.form.value.address?.['position']?.['lat'] || 0,
                    lon: this.form.value.address?.['position']?.['lon'] || 0,
                },
                regionalStructure: this.form.value.address?.['regionalStructure'] || [],
                zip: null,
            },
        }, createOrganizationFrag);

        if (organization) {
            await this.afterCreatedFlow(organization.id, this.form.value.name!);
        }
    }

    private createPersonFormGroup(prefix: string) {
        return {
            [`${prefix}DateFrom`]: this.fb.control<Date | null>(new Date(), Validators.required),
            [`${prefix}ChosenPersonId`]: this.fb.control<number | null>(null),
            [`${prefix}TitleBeforeName`]: this.fb.control<string | null>(''),
            [`${prefix}Name`]: this.fb.control<string | null>(''),
            [`${prefix}Surname`]: this.fb.control<string | null>('', Validators.required),
            [`${prefix}TitleAfterName`]: this.fb.control<string | null>(''),
            [`${prefix}Email`]: this.fb.control<string | null>('', [Validators.required, Validators.email]),
            [`${prefix}Password`]: this.fb.control<string | null>('', Validators.required),
            [`${prefix}Role`]: this.fb.control<string | null>(null),
            [`${prefix}Sex`]: this.fb.control<string | null>('male'),
            [`${prefix}Note`]: this.fb.control<string | null>(''),
        };
    }

    // email, password and surname should only be required when person is not selected from existing persons
    private updateValidators(): void {
        // const representativeGroup = this.form.controls['representativePerson'] as FormGroup;
        // const contactGroup = this.form.controls['contactPerson'] as FormGroup;
        //
        // if (this.form.value['representativePerson']?.['representativeChosenPersonId'] !== null) {
        //     representativeGroup.controls['representativeEmail']?.clearValidators();
        //     representativeGroup.controls['representativePassword']?.clearValidators();
        //     representativeGroup.controls['representativeSurname']?.clearValidators();
        // } else {
        //     representativeGroup.controls['representativeEmail']?.setValidators([Validators.required, Validators.email]);
        //     representativeGroup.controls['representativePassword']?.setValidators([Validators.required]);
        //     representativeGroup.controls['representativeSurname']?.setValidators([Validators.required]);
        // }
        // representativeGroup.controls['representativeEmail']?.updateValueAndValidity();
        // representativeGroup.controls['representativePassword']?.updateValueAndValidity();
        // representativeGroup.controls['representativeSurname']?.updateValueAndValidity();
        //
        // if (this.form.value['contactPerson']?.['contactChosenPersonId'] !== null) {
        //     contactGroup.controls['contactEmail']?.clearValidators();
        //     contactGroup.controls['contactPassword']?.clearValidators();
        //     contactGroup.controls['contactSurname']?.clearValidators();
        // } else {
        //     contactGroup.controls['contactEmail']?.setValidators([Validators.required, Validators.email]);
        //     contactGroup.controls['contactPassword']?.setValidators([Validators.required]);
        //     contactGroup.controls['contactSurname']?.setValidators([Validators.required]);
        // }
        // contactGroup.controls['contactEmail']?.updateValueAndValidity();
        // contactGroup.controls['contactPassword']?.updateValueAndValidity();
        // contactGroup.controls['contactSurname']?.updateValueAndValidity();
    }

    private async afterCreatedFlow(organizationId: number, orgName: string): Promise<void> {
        const chcrmSetting = this.store.globalGlobalStore.organizationStore
            .settings()
            .find(s =>
                s.key === OrganizationSettingTypeEnum.DONATION_FORM ||
                s.key === OrganizationSettingTypeEnum.COLLECTION_CATEGORY
            );
        if (chcrmSetting?.id) {
            // this is only for chcrm
            this.openCrmWizardDialog(organizationId, orgName);
        } else {
            await this.store.userAssignRulesAndAccess(organizationId);
            await this.router.navigateByUrl(`/content/organizations/${organizationId}`);
        }
    }

    private openCrmWizardDialog(organizationId: number, orgName: string): void {
        const ref = this.dialogService.open(CrmNewOrganizationWizardDialogComponent,
            {
                header: orgName,
                styleClass: 'p-dialog-width-60',
                data: { organizationId, orgName },
            });
        ref.onClose.subscribe(async (result: boolean) => {
            if (result) {
                await this.router.navigateByUrl(`/content/organizations/${organizationId}`);
            }
        });
    }
}
